import React, { useEffect, useRef, useState } from 'react';
import { container } from '../../styles/themes';
import { fontHeading2, fontMegaBody } from '../../styles/typographyNew';
import { classNames } from '../../utils/style';
import { MenuTabs } from './MenuTabs';
import { createPortal } from 'react-dom';

import './blur.scss';
export interface TabsLayoutProps {
  title?: string;
  subtitle?: string;
  cards: any[];
  className?: string;
}

export const Tabs = ({
  title,
  subtitle,
  cards,
  className,
}: TabsLayoutProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSticky, setIsSticky] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const componentRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentTabsRef = tabsRef.current;
      const currentCardsRef = cardsRef.current;
      const tabsRect =
        tabsRef && currentTabsRef
          ? (currentTabsRef as Element).getBoundingClientRect()
          : { top: 0, bottom: 0 };
      const cardsRect =
        cardsRef && currentCardsRef
          ? (currentCardsRef as Element).getBoundingClientRect()
          : { top: 0, bottom: 0 };

      if (currentTabsRef && currentCardsRef) {
        setIsSticky(
          tabsRect.top <= 70 && cardsRect.bottom > 150 && cardsRect.top < 190,
        );
      } else {
        setIsSticky(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isSticky]);

  const setActiveSlide = (
    key: number,
    selectedIndex: number,
    length: number,
  ) => {
    const relativeIndex = (key - selectedIndex + length) % length;
    if (relativeIndex === 0) return 'relative scale-100 z-[10] mt-[40px]';
    else
      return 'absolute bottom-0 left-0 top-0 right-0 min-h-[800px] scale-[0.96] -translate-y-[52px] md:-translate-y-[30px] z-[5] overflow-hidden';
  };

  return (
    <div
      ref={componentRef}
      className={classNames(
        'Tabs flex flex-col gap-[44px]',
        container,
        'md:!px-0',
        className,
      )}
    >
      <div className="flex flex-col gap-24 max-w-[800px] mx-auto">
        {title && (
          <h2
            className={classNames(fontHeading2, 'text-center text-neutral-301')}
          >
            {title}
          </h2>
        )}
        {subtitle && (
          <p
            className={classNames(
              fontMegaBody,
              'text-center text-navy-80 max-w-610 m-auto',
            )}
          >
            {subtitle}
          </p>
        )}
      </div>
      <div className="relative">
        <div ref={tabsRef} className="relative">
          {isSticky &&
            createPortal(
              <div
                className={classNames(
                  'shadow-tabs border-b border-solid border-neutral-21 fixed left-0 right-0',
                  'top-[64px] z-30',
                )}
              >
                <MenuTabs
                  isSticky={isSticky}
                  selectedIndex={selectedIndex}
                  cards={cards}
                  componentRef={componentRef}
                  setSelectedIndex={setSelectedIndex}
                />
              </div>,
              document.body,
            )}
          <MenuTabs
            isSticky={false}
            selectedIndex={selectedIndex}
            cards={cards}
            componentRef={componentRef}
            setSelectedIndex={setSelectedIndex}
          />
        </div>
        <div
          ref={cardsRef}
          className={classNames(
            'mt-[21px] md:max-w-[1200px] mx-auto',
            'h-auto md:mt-24 relative',
          )}
        >
          {cards.map((card: any, key: number) => {
            return(
            <div
              key={key}
              className={classNames(
                'transform',
                setActiveSlide(key, selectedIndex, cards.length),
                'bg-white border border-[#DFE6ED] border-1 rounded-[20px]',
                'transition-all duration-500 ease-in-out',
              )}
            >
              {card.children}
            </div>
          )})}
        </div>
      </div>
    </div>
  );
};
